import { useContext } from 'react'

import { formatMoney } from 'accounting'

import {
  Box, Button, LoadingBubbles, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type {
  CampaignQuoteItemModel, CampaignQuoteTemplateItemModel, OrderItemModel,
} from '@models/types'

import ProductOptionsSelector from '@components/ProductOptionsSelector'

import useEditItemOptions from './hooks/useEditItemOptions'

const MODAL_KEY = 'EditItemOptionsModal'

const buildTitle = (initEntity: CampaignQuoteItemModel | CampaignQuoteTemplateItemModel | OrderItemModel) => {
  if (initEntity.type === 'CampaignQuoteItem') return 'Quote Item'
  if (initEntity.type === 'CampaignQuoteTemplateItem') return 'Template Item'

  return 'Order Item'
}

type ModalCallbacks = {
  closeAllModals?: () => void,
  closeModal: () => void,
  updateItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
}

const EditItemOptionsModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const { closeModal, closeAllModals, updateItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}

  const {
    callbacks: payloadCallbacks, entity: initEntity, modalSize = 'medium', validateFn: validateCampaignQuotePricesFn,
  } = modalPayload
  const { updateItem: updateItemFn } = payloadCallbacks || {}

  const editOrderItemPayload = useEditItemOptions(initEntity)
  const {
    callbacks: {
      selectProviderPriceListItemId,
      selectProviderPriceListItemOptionValue,
    },
    currentPrice,
    loadingAgencyPrice,
    filteredProviderPriceListItems,
    missingCampaignFields,
    selectedProviderPriceListItemId,
    selectedProviderPriceListItemOptions,
    selectedServiceProductId,
    validOrder,
    updating,
  } = editOrderItemPayload

  // To be used with handleCallbackAction
  const updateItemPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2304
      action: updateItemFn
        ? payload => updateItemFn(payload, requestOptions)
        // @ts-expect-error TS2304
        : () => console.log('No updateItemFn supplied', payload),
      afterAction: () => {
        if (validateCampaignQuotePricesFn){
          validateCampaignQuotePricesFn()
        }
        if (closeAllModals) return closeAllModals()

        return closeModal()
      },
    },
    entityParams: {
      item_type: 'orderable',
      price_list_item_id: selectedProviderPriceListItemId,
      options: selectedProviderPriceListItemOptions,
    },
    requestOptions,
    toastText: 'Item Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={MODAL_KEY}
      showModal={showModal}
      size={modalSize}
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title="Edit"
        titleSecondLine={buildTitle(initEntity)}
      />

      <SidebarModal.Content>
        {!!selectedServiceProductId && !missingCampaignFields.length && (
          <ProductOptionsSelector
            callbacks={{
              selectProviderPriceListItemId,
              // @ts-expect-error TS2322
              selectProviderPriceListItemOptionValue,
            }}
            providerPriceListItems={filteredProviderPriceListItems}
            quantityFormFieldBoxProps={{ marginTop: 'small' }}
            // @ts-expect-error TS2322
            selectedProviderPriceListItemId={selectedProviderPriceListItemId}
            selectedProviderPriceListItemOptions={selectedProviderPriceListItemOptions}
          />
        )}
      </SidebarModal.Content>

      {validOrder && (
        <SidebarModal.Footer>
          <Box flexDirection="column" width="auto">
            <Text color="bodyFontLightColor" fontSize="small">
              Total:
            </Text>

            <Text fontSize="medium" marginTop="small" width="auto">
              {loadingAgencyPrice ? <LoadingBubbles color="blue" /> : formatMoney(currentPrice.total)}
            </Text>
          </Box>

          <Box justifyContent="flex-end" marginLeft="auto" width="auto">
            <Button
              buttonStyle="primaryCreate"
              disabled={!validOrder}
              loading={updating}
              loadingBubbleColor="white"
              onClick={() => updateItem(updateItemPayload)}
              size="medium"
              width="auto"
            >
              Update Item
            </Button>
          </Box>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditItemOptionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
